/**
 * Autopilot case file (spec §7): eight collapsible sections in reading order.
 * Everything shown is what the platform recorded it WOULD have done; the
 * section headed "Not done" says so in its title. The only write is the
 * adjudication, which is the benchmark's label.
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, XCircle, Circle, MinusCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn, formatDateTime } from '../../lib/utils';
import { adjudicateAutopilot, DISPOSITION_LABEL, ADJUDICATION_LABEL, AGREEMENT_STYLE, AGREEMENT_LABEL, EXECUTION_LABEL, EXECUTION_STYLE, type IncidentAutopilot, type AutopilotStep, type AdjudicationVerdict, type Lean, type HypothesisCard, type ExpectedPatternMatch } from '../../services/autopilot';
import { Chip, DispositionChip } from './AutopilotBanner';

const LEAN_STYLE: Record<Lean, string> = {
  'supports-benign': 'bg-accent-green/15 text-accent-green border-accent-green/40',
  'supports-compromise': 'bg-accent-red/15 text-accent-red border-accent-red/40',
  neutral: 'bg-bg-tertiary text-text-secondary border-border-default',
  unobservable: 'bg-accent-yellow/15 text-accent-yellow border-accent-yellow/40',
};
const LEAN_LABEL: Record<Lean, string> = { 'supports-benign': 'supports benign', 'supports-compromise': 'supports compromise', neutral: 'neutral', unobservable: 'unobservable' };
const NOT_RUN_LABEL: Record<string, string> = {
  'not-licensed': 'not licensed at this tenant', 'blind-at-tenant': 'blind at this tenant (never returns rows)', 'cap-reached': 'query cap reached', 'budget-spent': 'tenant query budget spent',
  'no-entity': 'no entity to query with', 'not-published': 'preset not published', 'not-in-catalogue': 'not an approved query', 'already-run': 'already run for this incident', 'time-limit': 'time limit reached',
};
const READING_STYLE: Record<ExpectedPatternMatch['reading'], string> = {
  'expected-benign': 'bg-accent-green/15 text-accent-green border-accent-green/40',
  watch: 'bg-accent-yellow/15 text-accent-yellow border-accent-yellow/40',
  'never-benign': 'bg-accent-red/15 text-accent-red border-accent-red/40',
};
const READING_LABEL: Record<ExpectedPatternMatch['reading'], string> = { 'expected-benign': 'expected benign', watch: 'watch', 'never-benign': 'never benign' };
const TIER_STYLE: Record<string, string> = {
  'pre-authorised': 'bg-accent-green/15 text-accent-green border-accent-green/40',
  'requires-approval': 'bg-accent-yellow/15 text-accent-yellow border-accent-yellow/40',
  never: 'bg-accent-red/15 text-accent-red border-accent-red/40',
};

/** Clock with seconds — the cycle is measured in seconds, which formatDateTime drops. */
const fmtClock = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' }) : '—';
const fmtDelta = (ms: number): string => {
  const abs = Math.abs(ms);
  const text = abs < 60_000 ? `${(abs / 1000).toFixed(abs < 10_000 ? 1 : 0)} s` : abs < 3_600_000 ? `${Math.round(abs / 60_000)} min` : `${(abs / 3_600_000).toFixed(1)} h`;
  return ms < 0 ? `−${text}` : text;
};
const msBetween = (from: string | null | undefined, to: string | null | undefined): number | null =>
  from && to ? new Date(to).getTime() - new Date(from).getTime() : null;
/** "+32 s" style offset from a reference instant, or an em dash when either side is missing. */
const plus = (from: string | null | undefined, to: string | null | undefined): string => {
  const ms = msBetween(from, to);
  return ms === null ? '—' : `+${fmtDelta(ms)}`;
};

/**
 * The cycle, as a clock: when the incident reached Defender, when Autopilot
 * started on it, when it reached its verdict, and the human milestones beside
 * them. Arrival comes from dbo.Incidents (defenderCreatedAt) and falls back to
 * the record's own intake copy.
 */
function CycleClock({ arrivedAt, startedAt, completedAt, rounds, firstHumanActionAt, closedAt, closedBy }: { arrivedAt: string | null; startedAt: string; completedAt: string; rounds: number; firstHumanActionAt: string | null; closedAt: string | null; closedBy: string | null }) {
  const rows: Array<{ label: string; at: string | null; note: string }> = [
    { label: 'Arrived in Defender', at: arrivedAt, note: '' },
    { label: 'Autopilot started', at: startedAt, note: arrivedAt ? `${plus(arrivedAt, startedAt)} after arrival` : '' },
    { label: 'Autopilot verdict', at: completedAt, note: `${plus(startedAt, completedAt)} of work over ${rounds} round(s)${arrivedAt ? ` · ${plus(arrivedAt, completedAt)} after arrival` : ''}` },
    { label: 'First human action', at: firstHumanActionAt, note: firstHumanActionAt && arrivedAt ? `${plus(arrivedAt, firstHumanActionAt)} after arrival` : firstHumanActionAt ? '' : 'none yet' },
    { label: 'Closed', at: closedAt, note: closedAt ? `${closedBy ? `by ${closedBy}` : 'by the platform (Defender auto-close)'}${arrivedAt ? ` · ${plus(arrivedAt, closedAt)} after arrival` : ''}` : 'still open' },
  ];
  return (
    <dl className="grid grid-cols-[auto_auto_1fr] gap-x-4 gap-y-0.5 text-xs items-baseline">
      {rows.map((r) => (
        <div key={r.label} className="contents">
          <dt className="text-text-secondary">{r.label}</dt>
          <dd className="font-mono text-text-primary whitespace-nowrap">{fmtClock(r.at)}</dd>
          <dd className="text-text-secondary">{r.note}</dd>
        </div>
      ))}
    </dl>
  );
}

function Section({ title, subtitle, defaultOpen = true, children }: { title: string; subtitle?: string; defaultOpen?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-xl border border-border-default bg-bg-secondary">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-3 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-text-secondary" /> : <ChevronRight className="w-4 h-4 text-text-secondary" />}
        <span className="font-medium text-text-primary">{title}</span>
        {subtitle && <span className="text-xs text-text-secondary ml-2">{subtitle}</span>}
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </section>
  );
}

function Quote({ children }: { children: ReactNode }) {
  return <blockquote className="border-l-2 border-accent-blue/60 pl-3 text-sm text-text-primary whitespace-pre-wrap leading-relaxed">{children}</blockquote>;
}

function RowsTable({ rows }: { rows: unknown[] }) {
  const objects = rows.filter((r): r is Record<string, unknown> => !!r && typeof r === 'object');
  if (objects.length === 0) return <pre className="text-xs text-text-secondary whitespace-pre-wrap">{JSON.stringify(rows, null, 1)}</pre>;
  const columns = [...new Set(objects.flatMap((r) => Object.keys(r)))].slice(0, 14);
  return (
    <div className="overflow-x-auto rounded border border-border-default">
      <table className="text-xs w-full">
        <thead className="bg-bg-tertiary text-text-secondary"><tr>{columns.map((c) => <th key={c} className="px-2 py-1 text-left font-medium whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>
          {objects.map((r, i) => (
            <tr key={i} className="border-t border-border-default">
              {columns.map((c) => <td key={c} className="px-2 py-1 font-mono whitespace-nowrap max-w-xs truncate" title={String(r[c] ?? '')}>{typeof r[c] === 'object' ? JSON.stringify(r[c]) : String(r[c] ?? '')}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StepNode({ step }: { step: AutopilotStep }) {
  const [showRows, setShowRows] = useState(false);
  const greyed = step.notRun !== null;
  return (
    <li className={cn('relative pl-6 pb-5', greyed && 'opacity-70')}>
      <span className={cn('absolute left-0 top-1 w-3 h-3 rounded-full border-2', greyed ? 'border-border-default bg-bg-tertiary' : step.lean === 'supports-compromise' ? 'border-accent-red bg-accent-red/40' : step.lean === 'supports-benign' ? 'border-accent-green bg-accent-green/40' : 'border-accent-blue bg-accent-blue/30')} />
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className="font-mono text-xs text-text-primary">{step.presetId}</span>
        {step.presetName && step.presetName !== step.presetId && <span className="text-xs text-text-secondary">{step.presetName}</span>}
        <Chip className={greyed ? 'bg-bg-tertiary text-text-secondary border-border-default' : step.status === 'success' ? 'bg-accent-blue/15 text-accent-blue border-accent-blue/40' : 'bg-bg-tertiary text-text-secondary border-border-default'}>
          {greyed ? `not run — ${NOT_RUN_LABEL[step.notRun ?? ''] ?? step.notRun}` : step.status}{!greyed && step.rowCount !== null ? ` · ${step.rowCount} rows` : ''}{step.durationMs ? ` · ${(step.durationMs / 1000).toFixed(1)}s` : ''}
        </Chip>
        {step.lean && <Chip className={LEAN_STYLE[step.lean]}>{LEAN_LABEL[step.lean]}</Chip>}
      </div>
      <p className="text-xs text-text-secondary italic mb-1">{step.question}</p>
      {step.finding && <p className="text-sm text-text-primary">{step.finding}</p>}
      {step.rows.length > 0 && (
        <button onClick={() => setShowRows(!showRows)} className="text-xs text-accent-blue hover:underline mt-1">{showRows ? 'Hide rows' : `Show ${step.rows.length} row${step.rows.length === 1 ? '' : 's'}`}</button>
      )}
      {showRows && <div className="mt-2"><RowsTable rows={step.rows} /></div>}
    </li>
  );
}

function Hypothesis({ card, steps }: { card: HypothesisCard; steps: AutopilotStep[] }) {
  const supporting: Lean = card.reading === 'benign' ? 'supports-benign' : 'supports-compromise';
  const opposing: Lean = card.reading === 'benign' ? 'supports-compromise' : 'supports-benign';
  const colour = card.reading === 'benign' ? 'border-accent-green/40' : 'border-accent-red/40';
  return (
    <div className={cn('rounded-lg border p-3 space-y-2', colour)}>
      <div className="text-xs uppercase tracking-wider text-text-secondary">{card.reading === 'benign' ? 'Benign reading' : 'Compromise reading'}</div>
      {card.statements.map((s, i) => (
        <p key={i} className="text-sm text-text-primary"><span className="text-text-secondary">{s.rule}: </span>{s.text}</p>
      ))}
      {card.confirmingQueries.length > 0 && (
        <ul className="text-xs space-y-1">
          {card.confirmingQueries.map((q) => {
            const step = steps.find((s) => s.presetId === q);
            const state = !step || step.notRun ? 'open' : step.lean === supporting ? 'yes' : step.lean === opposing ? 'no' : 'neutral';
            const Icon = state === 'yes' ? CheckCircle2 : state === 'no' ? XCircle : state === 'neutral' ? MinusCircle : Circle;
            const cls = state === 'yes' ? 'text-accent-green' : state === 'no' ? 'text-accent-red' : 'text-text-secondary';
            return <li key={q} className={cn('flex items-center gap-1.5 font-mono', cls)}><Icon className="w-3.5 h-3.5" />{q}{step?.notRun ? <span className="font-sans not-italic text-text-secondary">— {NOT_RUN_LABEL[step.notRun] ?? step.notRun}</span> : null}</li>;
          })}
        </ul>
      )}
    </div>
  );
}

interface Props {
  tenantId: string;
  incidentId: string;
  data: IncidentAutopilot;
  onAdjudicated: (data: IncidentAutopilot) => void;
}

const VERDICTS: AdjudicationVerdict[] = ['agree', 'should-have-escalated', 'should-have-closed', 'wrong-reasoning-right-answer'];

export function AutopilotTab({ tenantId, incidentId, data, onAdjudicated }: Props) {
  const { run, human, agreement } = data;
  const rec = run.record;
  const [verdict, setVerdict] = useState<AdjudicationVerdict | null>(run.adjudication?.verdict ?? null);
  const [note, setNote] = useState(run.adjudication?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!rec) return <div className="text-sm text-text-secondary">The record for this run could not be read.</div>;

  const steps = rec.investigation.steps;
  const byRound = new Map<number, AutopilotStep[]>();
  for (const s of steps) byRound.set(s.round, [...(byRound.get(s.round) ?? []), s]);
  const rounds = [...byRound.keys()].sort((a, b) => a - b);
  const stepRef = (presetId: string | null) => (presetId ? <span className="font-mono text-xs text-accent-blue">{presetId}</span> : <span className="text-xs text-text-secondary">incident</span>);
  const tc = rec.intake.tenantContext as { thisRule?: { count?: number; benignClosureRate?: number }; blindQueries?: Array<{ presetId?: string } | string>; licensingNotes?: string[]; expectedPatterns?: ExpectedPatternMatch[] } | null;
  const offeredPatterns = tc?.expectedPatterns ?? [];
  const appliedPatterns = rec.verdict.expectedPatternsApplied ?? [];
  const appliedById = new Map(appliedPatterns.map((a) => [a.patternId, a]));
  const citedButNotOffered = appliedPatterns.filter((a) => !offeredPatterns.some((p) => p.patternId === a.patternId));
  const arrivedAt = human.arrivedAt ?? rec.intake.incident.createdDateTime ?? null;
  const notes = human.notes ?? [];
  const closing = human.transitions.find((t) => t.to.toLowerCase() === 'closed') ?? null;
  const closedBy = closing ? (closing.byName ?? (closing.byUserId ? 'an analyst' : null)) : null;

  const save = async () => {
    if (!verdict) return;
    setSaving(true); setError(null);
    const result = await adjudicateAutopilot(tenantId, incidentId, verdict, note);
    setSaving(false);
    if (!result.success) { setError(result.error ?? 'Could not record the adjudication'); return; }
    onAdjudicated({ ...data, run: { ...run, adjudication: result.data.adjudication } });
  };

  return (
    <div className="space-y-4">
      <Section title="1. Verdict">
        <div className="flex flex-wrap items-center gap-2">
          <DispositionChip disposition={rec.verdict.disposition} confidence={rec.verdict.confidence} />
          <Chip className="bg-bg-tertiary text-text-secondary border-border-default">risk {rec.verdict.riskScore} · priority {rec.verdict.priority}</Chip>
          <Chip className="bg-bg-tertiary text-text-secondary border-border-default">scoring rule: {rec.verdict.scoringRuleApplied}</Chip>
        </div>
        {rec.verdict.modelDisposition !== rec.verdict.disposition && (
          <p className="text-sm text-accent-yellow">The model proposed <strong>{DISPOSITION_LABEL[rec.verdict.modelDisposition]}</strong>; the code rule recorded <strong>{DISPOSITION_LABEL[rec.verdict.disposition]}</strong> because {rec.verdict.dispositionReason}.</p>
        )}
        <div className="space-y-2 text-sm text-text-primary leading-relaxed">
          <p>{rec.verdict.reasoning.ruleMeaning}</p>
          <p>{rec.verdict.reasoning.evidence}</p>
          <p>{rec.verdict.reasoning.decision}</p>
        </div>
        <div className="rounded-lg border border-border-default p-3">
          <div className="text-xs uppercase tracking-wider text-text-secondary mb-2">Cycle</div>
          <CycleClock arrivedAt={arrivedAt} startedAt={rec.startedAt} completedAt={rec.completedAt} rounds={rec.investigation.rounds} firstHumanActionAt={human.firstHumanActionAt} closedAt={human.closedAt} closedBy={closedBy} />
        </div>
      </Section>

      <Section title="2. What it was looking at" subtitle={`${rec.intake.incident.alertCount} alert(s) · ${rec.intake.rules.length} matched rule(s)`}>
        {rec.intake.rules.length === 0 && <p className="text-sm text-text-secondary">No playbook knowledge matched this incident's rule; the generic playbook was used.</p>}
        {rec.intake.rules.map((r, i) => (
          <div key={i} className="rounded-lg border border-border-default p-3 space-y-1">
            <div className="text-sm font-medium text-text-primary">{r.rules.join(' / ')}{r.source && <span className="text-xs text-text-secondary ml-2">{r.source}</span>}</div>
            <p className="text-sm text-text-secondary">{r.meaning}</p>
            {r.scoreHint && <p className="text-xs text-text-secondary">Score hint: {r.scoreHint}</p>}
          </div>
        ))}
        <div className="grid md:grid-cols-2 gap-3">
          <Hypothesis card={rec.intake.hypotheses.benign} steps={steps} />
          <Hypothesis card={rec.intake.hypotheses.compromise} steps={steps} />
        </div>
        <p className="text-xs text-text-secondary">
          Tenant context: {tc?.thisRule ? `this rule fired ${tc.thisRule.count ?? '?'} times here in 30 days (${tc.thisRule.benignClosureRate !== undefined ? `${Math.round((tc.thisRule.benignClosureRate ?? 0) * 100)}% closed benign` : 'closure rate unknown'})` : 'no base rate for this rule'}
          {tc?.blindQueries?.length ? ` · blind queries: ${tc.blindQueries.map((b) => (typeof b === 'string' ? b : b.presetId)).join(', ')}` : ''}
          {tc?.licensingNotes?.length ? ` · ${tc.licensingNotes.join('; ')}` : ''}
        </p>
        <div className="rounded-lg border border-border-default p-3 space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-xs uppercase tracking-wider text-text-secondary">Tenant knowledge offered</div>
            <div className="text-xs text-text-secondary">{offeredPatterns.length === 0 ? 'no expected-pattern row matched this incident' : `${appliedPatterns.filter((a) => a.row).length} of ${offeredPatterns.length} applied`}</div>
          </div>
          {offeredPatterns.length > 0 && (
            <ul className="space-y-1.5">
              {offeredPatterns.map((p) => {
                const applied = appliedById.get(p.patternId);
                return (
                  <li key={p.patternId} className="flex items-start gap-2 text-sm">
                    {applied ? <CheckCircle2 className="w-4 h-4 text-accent-green mt-0.5 flex-shrink-0" aria-label="applied" /> : <Circle className="w-4 h-4 text-text-secondary mt-0.5 flex-shrink-0" aria-label="not applied" />}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Chip className={READING_STYLE[p.reading]}>{READING_LABEL[p.reading]}</Chip>
                        <span className="text-xs text-text-secondary">{p.entityType}</span>
                        <span className="font-mono text-xs text-text-primary">{p.entityValue}</span>
                        <span className="text-xs text-text-secondary">matched {p.matchedOn.entityType} <span className="font-mono">{p.matchedOn.value}</span>{p.scope !== 'any' ? ` · ${p.scope}` : ''}</span>
                      </div>
                      <div className="text-xs text-text-secondary">{p.rationale}{p.condition ? ` — condition: ${p.condition}` : ''}{p.confirmedBy ? ` — confirmed by ${p.confirmedBy}` : ''}</div>
                      {applied && <div className="text-xs text-text-primary">Applied: {applied.howItApplied}</div>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {citedButNotOffered.length > 0 && (
            <p className="text-xs text-accent-red">The model cited {citedButNotOffered.length} pattern id(s) it was not offered: {citedButNotOffered.map((a) => a.patternId).join(', ')} — treat the discount as unfounded.</p>
          )}
        </div>
      </Section>

      <Section title="3. Investigation timeline" subtitle={`${rec.investigation.followUpsRun} follow-up(s) in ${rec.investigation.rounds} round(s) · stopped: ${rec.investigation.stoppedBecause}${rec.investigation.budget.readable ? '' : ' · budget unreadable'}`}>
        <ol className="relative border-l border-border-default ml-1.5 pl-1">
          {rounds.map((round) => (
            <li key={round} className="list-none">
              <div className="relative -ml-[7px] mb-3 flex flex-wrap items-center gap-2">
                <span className="w-3 h-3 rounded-sm bg-accent-blue" />
                <span className="text-xs uppercase tracking-wider text-text-secondary">{round === 0 ? 'Round 0 — playbook first pass' : `Round ${round} — follow-up`}</span>
                {round === 0 && <span className="font-mono text-xs text-text-secondary">{fmtClock(rec.startedAt)}</span>}
                {round > 0 && rec.investigation.roundQuestions.filter((q) => q.round === round && q.at).map((q, i) => (
                  <span key={i} className="font-mono text-xs text-text-secondary" title="When this round's queries started, and when the risk stage had read their results">
                    {fmtClock(q.at)} · {plus(rec.startedAt, q.at)} after start{q.answeredAt ? ` · answered ${plus(q.at, q.answeredAt)} later` : ''}
                  </span>
                ))}
              </div>
              {round > 0 && rec.investigation.roundQuestions.filter((q) => q.round === round).map((q, i) => (
                <p key={i} className="text-xs text-text-secondary italic mb-2 pl-6">To answer: {q.question}</p>
              ))}
              <ul>{(byRound.get(round) ?? []).map((s, i) => <StepNode key={`${s.presetId}-${i}`} step={s} />)}</ul>
            </li>
          ))}
          <li className="list-none">
            <div className="relative -ml-[7px] flex flex-wrap items-center gap-2">
              <span className="w-3 h-3 rounded-sm bg-accent-green" />
              <span className="text-xs uppercase tracking-wider text-text-secondary">Verdict reached</span>
              <span className="font-mono text-xs text-text-secondary">{fmtClock(rec.completedAt)} · {plus(rec.startedAt, rec.completedAt)} after start</span>
            </div>
          </li>
        </ol>
      </Section>

      <Section title="4. Evidence balance">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-accent-green mb-1">For the verdict</div>
            <ul className="space-y-1 text-sm">{rec.verdict.evidenceFor.map((e, i) => <li key={i}>{stepRef(e.presetId)} — {e.reason}</li>)}{rec.verdict.evidenceFor.length === 0 && <li className="text-text-secondary">none recorded</li>}</ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-accent-red mb-1">Against</div>
            <ul className="space-y-1 text-sm">{rec.verdict.evidenceAgainst.map((e, i) => <li key={i}>{stepRef(e.presetId)} — {e.reason}</li>)}{rec.verdict.evidenceAgainst.length === 0 && <li className="text-text-secondary">none recorded</li>}</ul>
          </div>
        </div>
        {rec.verdict.unknowns.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-accent-yellow mb-1">Unknown</div>
            <ul className="space-y-1 text-sm">{rec.verdict.unknowns.map((u, i) => <li key={i}><span className="text-text-primary">{u.what}</span> <span className="text-text-secondary">— {u.why}</span></li>)}</ul>
          </div>
        )}
      </Section>

      <Section title="5. What it would have done — not done; the platform's opinion for comparison">
        {rec.next.closure && (
          <div>
            <div className="text-xs text-text-secondary mb-1">Closure it would have made: <span className="font-mono">{rec.next.closure.classification}</span> / <span className="font-mono">{rec.next.closure.determination}</span></div>
            <Quote>{rec.next.closure.comment}</Quote>
          </div>
        )}
        {rec.next.escalationNote && (
          <div>
            <div className="text-xs text-text-secondary mb-1">Escalation note for L2</div>
            <Quote>{rec.next.escalationNote}</Quote>
          </div>
        )}
        <div>
          <div className="text-xs text-text-secondary mb-1">Customer message · audience: <span className="font-medium">{rec.next.customerMessage.audience}</span></div>
          {rec.next.customerMessage.text ? <Quote>{rec.next.customerMessage.text}</Quote> : <p className="text-sm text-text-secondary">Nothing would have been sent.</p>}
        </div>
        <div>
          <div className="text-xs text-text-secondary mb-1">Actions</div>
          {rec.next.actions.length === 0 && <p className="text-sm text-text-secondary">No customer-side action.</p>}
          <ul className="space-y-1">
            {rec.next.actions.map((a, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                <Chip className={TIER_STYLE[a.authorisation]}>{a.authorisation}</Chip>
                <span className="text-text-primary">{a.action}</span>
                {a.target && <span className="font-mono text-xs text-text-secondary">→ {a.target}</span>}
                {a.condition && <span className="text-xs text-text-secondary">({a.condition})</span>}
                {a.wouldHaveProposed && <Chip className="bg-accent-blue/15 text-accent-blue border-accent-blue/40">would have proposed</Chip>}
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section title="6. What the platform did with the verdict" subtitle={run.execution ? EXECUTION_LABEL[run.execution.decision] : 'not evaluated'}>
        {run.execution ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Chip className={EXECUTION_STYLE[run.execution.decision]}>{EXECUTION_LABEL[run.execution.decision]}</Chip>
              {run.execution.executedAt && <Chip className="bg-bg-tertiary text-text-secondary border-border-default">closed {formatDateTime(run.execution.executedAt)}</Chip>}
              {run.execution.policyAt && <Chip className="bg-bg-tertiary text-text-secondary border-border-default" title="The tenant policy in force when the decision was taken">policy of {formatDateTime(run.execution.policyAt)}</Chip>}
            </div>
            {run.execution.reason && <Quote>{run.execution.reason}</Quote>}
            <p className="text-xs text-text-secondary">
              {run.execution.decision === 'executed'
                ? 'This incident was closed by Alexein Autopilot under the customer\'s policy, through the same close path an analyst uses. The customer sees an ordinary closure.'
                : run.execution.decision === 'would-execute'
                  ? 'The policy is in decide-only mode: this is the close the executor would have made. Nothing was closed.'
                  : 'The executor looked at this verdict and did not act; the reason above is the first guard that failed.'}
            </p>
          </div>
        ) : (
          <p className="text-sm text-text-secondary">
            The executor has not evaluated this record — it only looks at would-close verdicts from the last 24 hours, and only where the environment has an Autopilot policy table.
          </p>
        )}
      </Section>

      <Section title="7. What happened">
        <div className="flex flex-wrap items-center gap-2">
          <Chip className={AGREEMENT_STYLE[agreement]}>{AGREEMENT_LABEL[agreement]}</Chip>
          <Chip className="bg-bg-tertiary text-text-secondary border-border-default">status: {human.platformStatus ?? 'unknown'}</Chip>
          {human.classification && <Chip className="bg-bg-tertiary text-text-secondary border-border-default">{human.classification} / {human.determination ?? '—'}</Chip>}
          {arrivedAt && <Chip className="bg-bg-tertiary text-text-secondary border-border-default" title="When Autopilot reached its verdict, measured from the incident's arrival in Defender">Autopilot verdict {plus(arrivedAt, rec.completedAt)} after arrival</Chip>}
          {human.minutesToFirstAction !== null && <Chip className="bg-bg-tertiary text-text-secondary border-border-default">first human action after {human.minutesToFirstAction} min</Chip>}
          {human.minutesToClose !== null && <Chip className="bg-bg-tertiary text-text-secondary border-border-default">closed after {human.minutesToClose} min</Chip>}
        </div>
        <ul className="text-sm space-y-2">
          {human.transitions.map((t, i) => (
            <li key={i}>
              <div className="flex flex-wrap gap-2">
                <span className="text-text-secondary text-xs w-36">{formatDateTime(t.at)}</span>
                <span className="text-text-primary">{t.from ?? '—'} → {t.to}</span>
                <span className="text-text-secondary">{t.byName ?? (t.byUserId ? 'analyst' : 'platform')}</span>
                {t.actor === 'autopilot' && <Chip className="bg-accent-blue/15 text-accent-blue border-accent-blue/40" title="An executed Autopilot close; the customer sees an ordinary closure">Autopilot</Chip>}
              </div>
              {t.reason && <Quote>{t.reason}</Quote>}
            </li>
          ))}
          {human.transitions.length === 0 && <li className="text-text-secondary">No human action recorded yet.</li>}
        </ul>
        <div>
          <div className="text-xs text-text-secondary mb-1">Analyst notes{notes.length ? ` (${notes.length})` : ''}</div>
          {notes.length === 0 && <p className="text-sm text-text-secondary">No analyst notes on this incident.</p>}
          <ul className="space-y-2">
            {notes.map((n) => (
              <li key={n.noteId}>
                <div className="flex flex-wrap gap-2 text-xs text-text-secondary">
                  <span className="w-36">{formatDateTime(n.createdAt)}</span>
                  <span>{n.authorName ?? 'analyst'}</span>
                  {n.noteType !== 'comment' && <span className="font-mono">{n.noteType}</span>}
                </div>
                <Quote>{n.content}</Quote>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section title="8. Adjudication">
        {run.adjudication && <p className="text-xs text-text-secondary">Previous: {ADJUDICATION_LABEL[run.adjudication.verdict]} by {run.adjudication.by ?? 'unknown'}{run.adjudication.at ? ` at ${formatDateTime(run.adjudication.at)}` : ''}{run.adjudication.note ? ` — "${run.adjudication.note}"` : ''}</p>}
        {data.canAdjudicate === false ? (
          <p className="text-sm text-text-secondary">
            {run.adjudication ? 'This run has been adjudicated.' : 'Not yet adjudicated.'} Adjudication is the benchmark's label and is recorded by the closure approver; other roles read the case file without scoring it.
          </p>
        ) : (<>
        <div className="flex flex-wrap gap-2">
          {VERDICTS.map((v) => <Button key={v} size="sm" variant={verdict === v ? 'primary' : 'secondary'} onClick={() => setVerdict(v)}>{ADJUDICATION_LABEL[v]}</Button>)}
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Why — this note is the tuning backlog" className="w-full rounded-lg border border-border-default bg-bg-primary p-2 text-sm text-text-primary" />
        <div className="flex items-center gap-3">
          <Button size="sm" variant="primary" disabled={!verdict || saving} onClick={save}>{saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Record adjudication</Button>
          {error && <span className="text-xs text-accent-red">{error}</span>}
        </div>
        </>)}
      </Section>

      <Section title="9. Provenance" defaultOpen={false}>
        <dl className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <dt className="text-text-secondary">Run</dt><dd className="font-mono">{rec.runId}</dd>
          <dt className="text-text-secondary">Playbook</dt><dd className="font-mono">{rec.playbookId ?? '—'} v{rec.playbookVersion ?? '—'}</dd>
          <dt className="text-text-secondary">Model</dt><dd className="font-mono">{rec.modelDeployment} · {rec.pipelineVersion}</dd>
          <dt className="text-text-secondary">Prompt hashes</dt><dd className="font-mono">{Object.entries(rec.promptVersions).map(([k, v]) => `${k}:${v}`).join(' ')}</dd>
          <dt className="text-text-secondary">Queries run</dt><dd>{steps.filter((s) => s.notRun === null).length} ({rec.investigation.followUpsRun} follow-ups) · budget {rec.investigation.budget.readable ? `${rec.investigation.budget.usedAtStart ?? '?'}/${rec.investigation.budget.limit} used at start` : 'unreadable'}</dd>
          <dt className="text-text-secondary">Tokens</dt><dd>{rec.usage.promptTokens.toLocaleString()} in / {rec.usage.completionTokens.toLocaleString()} out over {rec.usage.modelCalls} calls</dd>
          <dt className="text-text-secondary">Duration</dt><dd>{(rec.usage.durationMs / 1000).toFixed(1)}s · {formatDateTime(rec.startedAt)} → {formatDateTime(rec.completedAt)}</dd>
          <dt className="text-text-secondary">Fingerprint</dt><dd className="font-mono">{rec.contentFingerprint?.slice(0, 16) ?? '—'}</dd>
        </dl>
      </Section>
    </div>
  );
}
